import type { CsvQuestion, ParsedQuestion } from '../types/community-questions';

const CSV_SEPARATOR = '|';

const parseQuotedCsvLine = (line: string): string[] => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 2;
      } else {
        inQuotes = !inQuotes;
        i++;
      }
    } else if (char === CSV_SEPARATOR && !inQuotes) {
      values.push(current);
      current = '';
      i++;
    } else {
      current += char;
      i++;
    }
  }

  values.push(current);
  return values;
};

export const parseCsvFile = (csvContent: string): ParsedQuestion[] => {
  const lines = csvContent.split('\n').filter((line) => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV file must contain at least a header and one data row');
  }

  const headers = parseQuotedCsvLine(lines[0]).map((h) => h.trim());
  const expectedHeaders = [
    'text',
    'description',
    'learning',
    'type',
    'opt1',
    'opt1_isCorrect',
    'opt2',
    'opt2_isCorrect',
    'opt3',
    'opt3_isCorrect',
    'opt4',
    'opt4_isCorrect',
  ];

  for (const expectedHeader of expectedHeaders) {
    if (!headers.includes(expectedHeader)) {
      throw new Error(`Missing required column: ${expectedHeader}`);
    }
  }

  const questions: ParsedQuestion[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseQuotedCsvLine(lines[i]).map((v) => v.trim());

    if (values.length !== headers.length) {
      throw new Error(`Row ${i + 1}: Expected ${headers.length} columns, got ${values.length}`);
    }

    const rowData: Record<string, string> = {};
    headers.forEach((header, index) => {
      rowData[header] = values[index];
    });

    const csvQuestion = rowData as unknown as CsvQuestion;

    if (!csvQuestion.text || !csvQuestion.type) {
      throw new Error(`Row ${i + 1}: Missing required fields (text and type)`);
    }

    if (!['question', 'poll'].includes(csvQuestion.type)) {
      throw new Error(`Row ${i + 1}: Type must be either 'question' or 'poll'`);
    }

    const parsedQuestion: ParsedQuestion = {
      type: csvQuestion.type,
      text: csvQuestion.text,
      optionsOrder: [],
      options: {},
      totalResponses: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    if (csvQuestion.description?.trim()) {
      parsedQuestion.description = csvQuestion.description;
    }
    if (csvQuestion.learning?.trim()) {
      parsedQuestion.learning = csvQuestion.learning;
    }

    const options = [
      { label: csvQuestion.opt1, isCorrect: csvQuestion.opt1_isCorrect },
      { label: csvQuestion.opt2, isCorrect: csvQuestion.opt2_isCorrect },
      { label: csvQuestion.opt3, isCorrect: csvQuestion.opt3_isCorrect },
      { label: csvQuestion.opt4, isCorrect: csvQuestion.opt4_isCorrect },
    ];

    options.forEach((option, index) => {
      const optionId = (index + 1).toString();
      if (option.label?.trim()) {
        parsedQuestion.options[optionId] = {
          label: option.label.trim(),
          responseCount: 0,
        };

        parsedQuestion.optionsOrder.push(optionId);

        if (csvQuestion.type === 'question' && option.isCorrect?.trim()) {
          const isCorrect = option.isCorrect.toLowerCase() === 'true' || option.isCorrect === '1';
          parsedQuestion.options[optionId].isCorrect = isCorrect;
        }
      }
    });

    if (Object.keys(parsedQuestion.options).length < 2) {
      throw new Error(`Row ${i + 1}: At least 2 options must be provided`);
    }

    if (csvQuestion.type === 'question') {
      const hasCorrectAnswer = Object.values(parsedQuestion.options).some((opt) => opt.isCorrect === true);
      if (!hasCorrectAnswer) {
        throw new Error(`Row ${i + 1}: Question type must have at least one correct answer`);
      }
    }

    questions.push(parsedQuestion);
  }

  return questions;
};
